import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trash2, ShoppingBag, Plus, Minus } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from 'sonner';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import PageLoadingState from '../components/PageLoadingState';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

function ImageModal({ isOpen, onClose, imageUrl, productName }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] md:max-w-[70vw] lg:max-w-[50vw] p-0 overflow-hidden bg-black/90 border-white/10">
        <DialogTitle className="sr-only">
          Enlarged view of {productName}
        </DialogTitle>
        <div className="relative w-full h-full flex items-center justify-center min-h-[50vh]">
          <img
            src={imageUrl}
            alt={productName}
            className="max-w-full max-h-[85vh] object-contain"
          />
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
            <p className="text-white text-center font-medium">{productName}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Cart() {
  const { setCartCount } = useApp();
  const [cart, setCart] = useState({ items: [] });
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageUrl: "",
    productName: "",
  });
  const navigate = useNavigate();

  const fetchUserProfile = useCallback(async () => {
    const token = sessionStorage.getItem('token');
    if (!token) return;
    try {
      const response = await axios.get(`${API}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
  }, []);

  const fetchCart = useCallback(async () => {
    const token = sessionStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(response.data);
      
      const productDetails = {};
      for (const item of response.data.items || []) {
        const prod = await axios.get(`${API}/products/${item.product_id}`);
        productDetails[item.product_id] = prod.data;
      }
      setProducts(productDetails);
      setCartCount(response.data.items?.length || 0);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  }, [setCartCount]);

  useEffect(() => {
    fetchCart();
    fetchUserProfile();
  }, [fetchCart, fetchUserProfile]);

  const removeFromCart = async (productId, color, note) => {
    const token = sessionStorage.getItem('token');
    let url = `${API}/cart/item/${productId}?`;
    if (color) url += `color=${encodeURIComponent(color)}&`;
    if (note) url += `note=${encodeURIComponent(note)}`;
    try {
      await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Item removed from cart');
      fetchCart();
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const updateQuantity = async (productId, color, note, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(productId, color, note);
      return;
    }

    const token = sessionStorage.getItem('token');
    try {
      await axios.put(`${API}/cart/update`, 
        { product_id: productId, quantity: newQuantity, color: color, note: note },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchCart();
    } catch (error) {
      toast.error('Failed to update quantity');
    }
  };

  const calculateTotal = () => {
    return cart.items?.reduce((total, item) => {
      const product = products[item.product_id];
      return total + (product?.price || 0) * item.quantity;
    }, 0) || 0;
  };

  const total = calculateTotal();
  const city = userProfile?.address?.city?.toLowerCase() || '';
  const isBangalore = city.includes('bangalore') || city.includes('bengaluru');
  const shippingLimit = isBangalore ? 500 : 1500;
  const shippingFee = (total >= shippingLimit || total === 0) ? 0 : 150;
  const finalTotal = total + shippingFee;

  if (loading) {
    return (
      <PageLoadingState title="Loading your cart" />
    );
  }

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 pb-20" data-testid="cart-page">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-12" data-testid="cart-title">Shopping Cart</h1>

          {cart.items && cart.items.length > 0 ? (
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-4">
                {cart.items.map((item, index) => {
                  const product = products[item.product_id];
                  if (!product) return null;

                  return (
                    <motion.div
                      key={`${item.product_id}-${item.color || 'none'}-${index}`}
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="glass-morphic rounded-2xl p-6"
                      data-testid={`cart-item-${item.product_id}`}
                    >
                      <div className="flex flex-col sm:flex-row gap-6 relative">
                        <div className="w-full sm:w-24 h-48 sm:h-24 rounded-lg overflow-hidden bg-muted/20 flex-shrink-0 cursor-zoom-in">
                          {product.images && product.images.length > 0 ? (
                            <img 
                              src={getImageUrl(product.images[0])} 
                              alt={product.name} 
                              className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" 
                              onClick={() => setImageModal({
                                isOpen: true,
                                imageUrl: getImageUrl(product.images[0]),
                                productName: product.name,
                              })}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <ShoppingBag className="h-8 w-8 text-muted-foreground" />
                            </div>
                          )}
                        </div>

                        <div className="flex-1">
                          <h3 className="text-xl font-medium mb-1 pr-8" data-testid={`cart-item-name-${item.product_id}`}>{product.name}</h3>
                          {item.color && (
                            <p className="text-primary text-sm font-medium mb-1">Colour: {item.color}</p>
                          )}
                          {item.note && (
                            <div className="bg-white/5 border border-white/10 rounded-lg p-2 mb-3">
                              <p className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Custom Note</p>
                              <p className="text-sm italic">"{item.note}"</p>
                            </div>
                          )}
                          <p className="text-muted-foreground text-sm line-clamp-2 mb-4">{product.description}</p>
                          <div className="flex flex-wrap items-center justify-between gap-4 mt-auto">
                            <span className="text-2xl font-semibold text-primary" data-testid={`cart-item-price-${item.product_id}`}>₹{product.price.toFixed(2)}</span>
                            <div className="flex items-center gap-2 bg-muted/20 rounded-full p-1 border border-white/10">
                              <button
                                onClick={() => updateQuantity(item.product_id, item.color, item.note, item.quantity - 1)}
                                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
                                data-testid={`decrease-quantity-${item.product_id}`}
                              >
                                <Minus className="h-3 w-3" />
                              </button>
                              <span className="w-6 text-center font-medium" data-testid={`cart-item-quantity-${item.product_id}`}>{item.quantity}</span>
                              <button
                                onClick={() => updateQuantity(item.product_id, item.color, item.note, item.quantity + 1)}
                                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
                                data-testid={`increase-quantity-${item.product_id}`}
                              >
                                <Plus className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        </div>

                        <button
                          onClick={() => removeFromCart(item.product_id, item.color, item.note)}
                          className="absolute top-0 right-0 text-destructive hover:text-destructive/80 transition-colors duration-300"
                          data-testid={`remove-item-${item.product_id}`}
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              <div className="lg:col-span-1">
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="glass-morphic rounded-2xl p-6 sticky top-32"
                  data-testid="cart-summary"
                >
                  <h2 className="text-2xl font-semibold mb-6">Order Summary</h2>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between text-muted-foreground">
                      <span>Subtotal</span>
                      <span data-testid="subtotal">₹{total.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Shipping</span>
                      <span data-testid="shipping-fee">
                        {shippingFee === 0 ? (
                          <span className="text-accent font-semibold">FREE</span>
                        ) : (
                          `₹${shippingFee.toFixed(2)}`
                        )}
                      </span>
                    </div>
                    {total < shippingLimit && total > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm text-accent" data-testid="free-shipping-message">
                          Add ₹{(shippingLimit - total).toFixed(2)} more for free shipping!
                        </p>
                        <div className="bg-white/5 rounded-xl p-4 border border-white/10 space-y-2">
                          <p className="text-xs text-muted-foreground flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                            Free delivery in Bangalore above ₹500
                          </p>
                          <p className="text-xs text-muted-foreground flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                            Free delivery rest of India above ₹1500
                          </p>
                        </div>
                      </div>
                    )}
                    <div className="pt-4 border-t border-white/10">
                      <div className="flex justify-between text-xl font-semibold">
                        <span>Total</span>
                        <span className="text-primary" data-testid="total-amount">₹{finalTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={() => navigate('/checkout')}
                    className="w-full rounded-full bg-primary hover:bg-primary/90 text-white py-6 text-lg shadow-[0_0_20px_rgba(139,92,246,0.3)] hover:shadow-[0_0_30px_rgba(139,92,246,0.5)] transition-all duration-300"
                    data-testid="proceed-to-checkout"
                  >
                    Proceed to Checkout
                  </Button>
                </motion.div>
              </div>
            </div>
          ) : (
            <div className="text-center py-20" data-testid="empty-cart">
              <ShoppingBag className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
              <p className="text-xl text-muted-foreground mb-6">Your cart is empty</p>
              <Button
                onClick={() => navigate('/products')}
                className="rounded-full bg-primary hover:bg-primary/90 text-white px-8 py-6 shadow-[0_0_20px_rgba(139,92,246,0.3)] transition-all duration-300"
                data-testid="browse-products-button"
              >
                Browse Products
              </Button>
            </div>
          )}
        </motion.div>
      </div>

      <ImageModal
        isOpen={imageModal.isOpen}
        onClose={() => setImageModal({ ...imageModal, isOpen: false })}
        imageUrl={imageModal.imageUrl}
        productName={imageModal.productName}
      />
    </div>
  );
}
